import { motion } from 'framer-motion';
import {
  BookOpen,
  Brain,
  Briefcase,
  Building2,
  Calendar,
  Code,
  FileText,
  GraduationCap,
  Lock,
  MessageSquare,
  Network,
  PencilIcon,
  Phone,
  Play,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Xarrow from 'react-xarrows';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import BouncingImage from '../BouncingImage';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import OnboardingModal from '../OnboardingModal';
import TutorialModal from '../TutorialModal';
import { TUTORIAL_STEPS } from './config/tutorialConfig';

// Custom LinkedIn icon component
const LinkedInIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const JobProgressFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userProfile,
    updateTutorialFlag,
    nodes,
    completedNodes,
    setCompletedNodes,
    hasOnboarded,
  } = useContext(GlobalContext);
  const { isAuthenticated } = useAuth();

  // Alert that comes from navigation
  const [alert, setAlert] = useState({ visible: false, message: '', link: '' });

  // Tutorial related elements
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);

  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  // Add refs for tutorial nodes
  const nodeRefs = {
    start: useRef(null),
    roles: useRef(null),
    process: useRef(null),
    navbar: useRef(null),
  };

  useEffect(() => {
    if (userProfile)
      setShowTutorial(
        userProfile.onboarding_progress.hasSeenProgressTreeTutorial !== true &&
          userProfile.has_onboarded === true
      );
  }, [userProfile]);

  useEffect(() => {
    // Check if there's an alert passed from the LessonInterface
    if (location.state && location.state.alert) {
      setAlert(location.state.alert);
    }
  }, [location.state]);

  // Add effect to handle body scroll during tutorial
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  // Modify the effect to use a non-interfering approach
  useEffect(() => {
    const navLinksContainer = document.querySelector(
      '.md\\:items-center.md\\:justify-end.gap-2.md\\:gap-3'
    );
    if (navLinksContainer) {
      const navLinks = Array.from(navLinksContainer.children).filter((link) => {
        const text = link.textContent.trim().toLowerCase();
        return !text.includes('lesson tree') && !text.includes('logout');
      });

      // Create a non-interfering reference element
      const highlightContainer = document.createElement('div');
      highlightContainer.style.position = 'absolute';
      highlightContainer.style.pointerEvents = 'none'; // Allow clicks to pass through
      highlightContainer.style.top = navLinks[0].offsetTop + 'px';
      highlightContainer.style.left = navLinks[0].offsetLeft + 'px';
      highlightContainer.style.width =
        navLinks[navLinks.length - 1].offsetLeft +
        navLinks[navLinks.length - 1].offsetWidth -
        navLinks[0].offsetLeft +
        'px';
      highlightContainer.style.height = navLinks[0].offsetHeight + 'px';
      highlightContainer.style.zIndex = '-1'; // Place behind the actual links

      navLinksContainer.appendChild(highlightContainer);
      nodeRefs.navbar.current = highlightContainer;
    }
  }, []);

  const handleNextStep = () => {
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenProgressTreeTutorial', true);
  };

  const clearProgress = async () => {
    setCompletedNodes([]);
    try {
      await axios.put(`/api/v1/user/${userProfile.user_id}/completed-lessons`, {
        completed_lessons: [],
      });
    } catch (error) {
      console.error('Error clearing progress:', error.response?.data?.detail || error.message);
    }
  };

  const findParents = (nodeId) => {
    return Object.values(nodes)
      .filter((node) => node.children && node.children.includes(nodeId))
      .map((node) => node.id);
  };

  const isNodeAvailable = (nodeId) => {
    if (nodeId === 'start') return true;
    const parents = findParents(nodeId);

    const lessonCompleted = completedNodes.includes(nodeId);
    if (lessonCompleted) {
      return true;
    }

    return parents.some((parentId) => completedNodes.includes(parentId));
  };

  const handleNodeClick = (node) => {
    if (!isNodeAvailable(node.id)) {
      const parents = findParents(node.id)
        .map((id) => nodes[id].title)
        .join(' or ');
      return;
    }

    navigate(node.path);
  };

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef.current?.getBoundingClientRect();

    if (!rect) return null;

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (currentStep.id === 'navbar') {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
          '--arrow-orientation': 'up',
        };
      }

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
        };
      }

      return {
        top: rect.top + rect.height / 2 - 100,
        left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
        width: '300px',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  const renderNode = (node) => {
    const isAvailable = isNodeAvailable(node.id);
    const isCompleted = completedNodes.includes(node.id);
    const parents = findParents(node.id)
      .map((id) => nodes[id].title)
      .join(' or ');

    // Create organic path offsets for a more artistic flow
    const getPathOffset = (nodeId, x, y) => {
      const offsets = {
        start: { x: 0, y: 0 },
        roles: { x: -30, y: 0 },
        process: { x: 40, y: 0 },
      };

      // Create a sine wave pattern for more organic positioning
      const waveOffset = Math.sin(y * 0.5) * 25;
      const baseOffset = offsets[nodeId] || { x: waveOffset, y: 0 };

      return baseOffset;
    };

    // Get appropriate icon for each node
    const getNodeIcon = (nodeId, title) => {
      const iconMap = {
        start: Play,
        intro: BookOpen,
        roles: Users,
        process: PencilIcon,
        application: Briefcase,
        linkedin: LinkedInIcon,
        resume: FileText,
        career: Target,
        networking: Network,
        phone: Phone,
        technical: Code,
        behavioral: MessageSquare,
        leetcode: Zap,
        clubs: Trophy,
        fairs: Calendar,
        alternative: Building2,
        interviews: Brain,
        getting: GraduationCap,
      };

      // Fallback based on title keywords
      if (iconMap[nodeId]) return iconMap[nodeId];

      if (title.toLowerCase().includes('technical') || title.toLowerCase().includes('code'))
        return Code;
      if (title.toLowerCase().includes('interview') || title.toLowerCase().includes('behavioral'))
        return MessageSquare;
      if (title.toLowerCase().includes('application') || title.toLowerCase().includes('job'))
        return Briefcase;
      if (title.toLowerCase().includes('resume') || title.toLowerCase().includes('cv'))
        return FileText;
      if (title.toLowerCase().includes('network') || title.toLowerCase().includes('people'))
        return Network;
      if (title.toLowerCase().includes('career') || title.toLowerCase().includes('goal'))
        return Target;
      if (title.toLowerCase().includes('linkedin')) return LinkedInIcon;
      if (title.toLowerCase().includes('phone') || title.toLowerCase().includes('call'))
        return Phone;
      if (title.toLowerCase().includes('fair') || title.toLowerCase().includes('event'))
        return Calendar;
      if (title.toLowerCase().includes('club') || title.toLowerCase().includes('achievement'))
        return Trophy;
      if (title.toLowerCase().includes('leetcode') || title.toLowerCase().includes('coding'))
        return Zap;

      return BookOpen; // Default icon
    };

    const offset = getPathOffset(node.id, node.x, node.y);
    const IconComponent = getNodeIcon(node.id, node.title);

    return (
      <div
        key={node.id}
        id={node.id}
        className="flex justify-center items-center absolute"
        style={{
          top: `${node.y * 120 + offset.y}px`,
          left: `${node.x * 180 + offset.x}px`,
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
        onMouseEnter={() => setHoveredNodeId(node.id)}
        onMouseLeave={() => setHoveredNodeId(null)}
        ref={nodeRefs[node.id] || null}
      >
        <div className="relative">
          <motion.button
            onClick={() => handleNodeClick(node)}
            whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
            whileTap={{ scale: 0.95 }}
            className={`
                            relative w-16 h-16 flex items-center justify-center rounded-full 
                            transition-all duration-300 hover:shadow-2xl border-3 backdrop-blur-xs
                            ${
                              isCompleted
                                ? 'bg-linear-to-br from-green-400 to-green-500 border-green-300 shadow-green-300/50 shadow-xl'
                                : isAvailable
                                  ? 'bg-linear-to-br from-blue-400 to-purple-500 border-blue-300 shadow-blue-300/50 shadow-xl'
                                  : 'bg-linear-to-br from-gray-200 to-gray-300 border-gray-200 shadow-gray-200/30 shadow-lg'
                            }
                            ${!isAvailable ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-2 hover:shadow-2xl transform-gpu'}
                        `}
          >
            {!isAvailable && <Lock className="w-6 h-6 text-gray-500" />}

            {(isAvailable || isCompleted) && (
              <IconComponent className="w-6 h-6 text-white drop-shadow-xs" />
            )}
          </motion.button>

          {/* Node title below the circle */}
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 text-center">
            <span className="text-xs font-semibold text-gray-700 whitespace-nowrap bg-white/90 backdrop-blur-xs px-3 py-1.5 rounded-full shadow-lg border border-white/40">
              {node.title}
            </span>
          </div>

          {!isAvailable && hoveredNodeId === node.id && (
            <div className="absolute opacity-100 transition-opacity duration-200 z-20 whitespace-nowrap px-3 py-2 bg-gray-800 text-white text-sm rounded-md -top-16 left-1/2 transform -translate-x-1/2">
              Complete {parents} first
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConnections = () => {
    return Object.values(nodes).flatMap((node) =>
      (node.children || []).map((childId) => {
        const childNode = nodes[childId];
        const isParentCompleted = completedNodes.includes(node.id);
        const isChildCompleted = completedNodes.includes(childId);

        // Dynamic connection colors based on completion status
        let connectionColor = '#e5e7eb'; // Default gray
        if (isParentCompleted && isChildCompleted) {
          connectionColor = '#10b981'; // Green for completed path
        } else if (isParentCompleted) {
          connectionColor = '#3b82f6'; // Blue for available path
        }

        return (
          <Xarrow
            key={`${node.id}-${childId}`}
            start={node.id}
            end={childId}
            color={connectionColor}
            strokeWidth={4}
            headSize={0}
            path="smooth"
            curveness={0.4}
            dashness={isParentCompleted ? false : { strokeLen: 8, nonStrokeLen: 8 }}
            startAnchor="bottom"
            endAnchor="top"
            zIndex={5}
            animateDrawing={0.5}
          />
        );
      })
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto p-4 space-y-6">
        {alert.visible && (
          <div className="fixed inset-0 z-51">
            <TutorialModal
              unlockedItem={alert.title}
              description={alert.description}
              showTutorialModal={alert.visible}
              link={alert.link}
            />
          </div>
        )}
        {isAuthenticated && !hasOnboarded && <OnboardingModal />}
        <TutorialOverlay />

        {/* Progress Tree Container */}
        <div className="relative flex justify-center items-center">
          <div className="relative" style={{ height: '1000px', width: '900px' }}>
            {Object.values(nodes).map(renderNode)}
            {renderConnections()}

            {/* Left Bouncing Image - positioned relative to roles node */}
            <div
              className="absolute"
              style={{
                top: `${nodes['roles'].y * 120 + 30}px`,
                left: `${nodes['roles'].x * 180 - 300}px`,
                transform: 'translateX(-50%)',
                zIndex: 10,
              }}
            >
              <BouncingImage width={10} imageLink="/trainingcar.png" />
            </div>

            {/* Text box positioned to the right of roles node */}
            <div
              className="absolute"
              style={{
                top: `${nodes['roles'].y * 120 - 60}px`,
                left: `${nodes['roles'].x * 180 + 130}px`,
                zIndex: 10,
              }}
            >
              <h1 className="text-5xl font-semibold text-gray-800 mb-2"> Complete lessons</h1>
              <p className="text-2xl text-gray-600">
                to learn more about the software engineering recruiting process and unlock features!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobProgressFlow;
